import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ExternalLink,
  RotateCcw,
  Sparkles,
  Copy,
  Check,
  Twitter,
  Linkedin,
} from "lucide-react";
import { RESULTS } from "@/constants/testIds";

export default function ResultsView({
  blurb,
  tools,
  firstName,
  shareUrl,
  leadId,
  sourceRef,
  showRestart = true,
}) {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  function copyLink() {
    if (!shareUrl) return;
    const url = `${shareUrl}?ref=cp`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }

  function trackToolClick(toolId) {
    // Fire-and-forget. Prefer sendBeacon (survives page unload), fall back to fetch keepalive.
    if (!toolId) return;
    const payload = JSON.stringify({ lead_id: leadId || null, tool_id: toolId, ref: sourceRef || null });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/tool-clicks`;
    try {
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: "application/json" });
        navigator.sendBeacon(url, blob);
        return;
      }
    } catch {
      // fall through to fetch fallback
    }
    try {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
        credentials: "include",
      }).catch(() => {});
    } catch {
      // swallow — never block the user's navigation
    }
  }

  const shareText = encodeURIComponent(
    `I just found my AI tool stack on AI Operator Hub: ${tools.map((t) => t.name).join(", ")}.`
  );
  const twitterShareUrl = shareUrl ? `${shareUrl}?ref=tw` : "";
  const linkedinShareUrl = shareUrl ? `${shareUrl}?ref=li` : "";
  const encodedTwUrl = encodeURIComponent(twitterShareUrl);
  const encodedLiUrl = encodeURIComponent(linkedinShareUrl);
  const twitterUrl = `https://twitter.com/intent/tweet?text=${shareText}&url=${encodedTwUrl}`;
  const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedLiUrl}`;

  function restart() {
    sessionStorage.removeItem("quiz_answers");
    navigate("/quiz");
  }

  return (
    <div data-testid={RESULTS.container} className="fade-in-up">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0f766e]">
        {firstName ? `${firstName}'s AI tool stack` : "Your AI tool stack"}
      </span>
      <h1 className="font-display mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
        Here's where to start.
      </h1>

      <div
        data-testid={RESULTS.blurb}
        className="mt-6 p-5 sm:p-6 rounded-xl border border-[#0f766e]/30 bg-[#ccfbf1]/50"
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-white grid place-items-center text-[#0f766e] shrink-0 border border-[#0f766e]/20">
            <Sparkles className="w-4 h-4" />
          </div>
          <p className="text-slate-800 leading-relaxed">{blurb}</p>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
        {tools.map((tool, idx) => (
          <article
            key={tool.id}
            data-testid={RESULTS.toolCard(tool.id)}
            className="border border-slate-200 bg-white rounded-xl p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-[#0f766e]"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#0f766e] bg-[#ccfbf1] px-2 py-1 rounded">
                Pick #{idx + 1}
              </span>
              <span className="text-xs text-slate-400">{tool.categories[0]}</span>
            </div>
            <h3 className="font-display text-xl font-semibold text-slate-900">{tool.name}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{tool.tagline}</p>
            <a
              href={tool.affiliate_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={RESULTS.toolCta(tool.id)}
              onClick={() => trackToolClick(tool.id)}
              onAuxClick={(e) => { if (e.button === 1) trackToolClick(tool.id); }}
              className="mt-6 inline-flex items-center justify-center gap-2 h-11 bg-[#0f766e] hover:bg-[#115e59] text-white rounded-md font-medium transition-colors"
            >
              Visit {tool.name}
              <ExternalLink className="w-4 h-4" />
            </a>
            <p className="mt-2 text-[11px] text-slate-400 text-center">
              Official website
            </p>
          </article>
        ))}
      </div>

      {/* Share card */}
      {shareUrl && (
        <div
          data-testid="results-share-card"
          className="mt-10 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-6 sm:p-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0f766e]">
                Share your stack
              </span>
              <h3 className="font-display mt-2 text-xl font-semibold text-slate-900">
                Send this page to your team
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Anyone with the link can view your AI tool recommendations. Your email
                stays private.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 md:items-center">
              <div className="flex items-center bg-white border border-slate-200 rounded-md overflow-hidden">
                <input
                  readOnly
                  value={shareUrl}
                  data-testid="results-share-url-input"
                  onFocus={(e) => e.target.select()}
                  className="px-3 h-10 text-sm text-slate-600 w-full sm:w-72 outline-none bg-transparent"
                />
                <button
                  onClick={copyLink}
                  data-testid="results-copy-link-btn"
                  aria-label="Copy share link"
                  className="h-10 px-3 border-l border-slate-200 hover:bg-slate-50 text-slate-600 transition-colors"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-[#0f766e]" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={twitterUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="results-share-twitter"
                  aria-label="Share on Twitter"
                  className="h-10 w-10 grid place-items-center rounded-md border border-slate-200 hover:border-[#0f766e] hover:text-[#0f766e] text-slate-600 transition-colors"
                >
                  <Twitter className="w-4 h-4" />
                </a>
                <a
                  href={linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="results-share-linkedin"
                  aria-label="Share on LinkedIn"
                  className="h-10 w-10 grid place-items-center rounded-md border border-slate-200 hover:border-[#0f766e] hover:text-[#0f766e] text-slate-600 transition-colors"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRestart && (
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 pt-8 border-t border-slate-200">
          <p className="text-sm text-slate-500">
            Got different priorities? Retake the quiz any time.
          </p>
          <Button
            variant="outline"
            onClick={restart}
            data-testid={RESULTS.restartBtn}
            className="border-slate-200 text-slate-700 hover:bg-slate-50"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Start over
          </Button>
        </div>
      )}
    </div>
  );
}
